// src/pages/MonthlyWinner.jsx
import { useEffect, useMemo, useState } from "react";
import { CalendarDays, Crown, RefreshCw, Trophy, UserRound, Zap } from "lucide-react";
import { fetchMonthlyAvailable, fetchMonthlyWinner } from "../api/fplApi";

function monthLabel(month) {
  return new Date(2000, month - 1, 1).toLocaleString(undefined, { month: "long" });
}

const now = new Date();
const DEFAULT_YEAR = now.getFullYear();
const DEFAULT_MONTH = now.getMonth() === 0 ? 12 : now.getMonth();

const chipLabel = (chip) => {
  if (!chip) return null;
  const labels = {
    "3xc": "Triple Captain",
    triple_captain: "Triple Captain",
    bboost: "Bench Boost",
    bench_boost: "Bench Boost",
    freehit: "Free Hit",
    wildcard: "Wildcard",
  };
  return labels[String(chip).toLowerCase()] || chip;
};

const normalizedPlayerName = (name = "") =>
  String(name)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const captainPortrait = (captain) => {
  if (!captain) return null;
  if (normalizedPlayerName(captain.name) === "joao pedro") return "/assets/players/joao-pedro-chelsea.png";
  if (captain.code) {
    return `https://resources.premierleague.com/premierleague/photos/players/250x250/p${captain.code}.png`;
  }
  return captain.team_code
    ? `https://fantasy.premierleague.com/dist/img/shirts/standard/shirt_${captain.team_code}-110.png`
    : null;
};

const captainFallback = (captain) =>
  captain?.team_code
    ? `https://fantasy.premierleague.com/dist/img/shirts/standard/shirt_${captain.team_code}-110.png`
    : null;

function CompactGwCard({ item }) {
  const { gw, points, hits, net, chip, captain } = item;
  const portrait = captainPortrait(captain);
  const fallback = captainFallback(captain);
  const hasHit = Number(hits || 0) > 0;

  return (
    <article className="motm-shot-gw">
      <div className="motm-shot-gw-top">
        <span>GW {gw}</span>
        {chip && <b><Zap size={10} />{chipLabel(chip)}</b>}
      </div>

      <div className="motm-shot-gw-player">
        <span className="motm-shot-gw-watermark">{String(gw).padStart(2, "0")}</span>
        {portrait ? (
          <img
            src={portrait}
            alt={captain?.name || "Captain"}
            loading="lazy"
            onError={(event) => {
              if (fallback && event.currentTarget.src !== fallback) event.currentTarget.src = fallback;
              else event.currentTarget.style.display = "none";
            }}
          />
        ) : <UserRound aria-hidden="true" />}
        <i>C</i>
      </div>

      <div className="motm-shot-gw-info">
        <div className="motm-shot-gw-captain">
          <span>Captain</span>
          <strong>{captain?.name || "Unavailable"}</strong>
          <small>{captain?.team || "—"}</small>
        </div>
        <div className="motm-shot-gw-score">
          <span>GW score</span>
          <strong>{Number(points || 0)}<small> pts</small></strong>
          {hasHit && <em>−{Number(hits)} hit · Net {Number(net || 0)}</em>}
        </div>
      </div>
    </article>
  );
}

const LoadingCard = () => <div className="motm-shot-loading animate-pulse" />;

export default function MonthlyWinner({ leagueId = 136323, title = "S16" }) {
  const [year, setYear] = useState(DEFAULT_YEAR);
  const [month, setMonth] = useState(DEFAULT_MONTH);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const isThe100 = title === "THE 100";
  const competitionClass = isThe100 ? "motm-the100" : "motm-s16";
  const competitionName = isThe100 ? "THE 100" : "SEASON 16";
  const years = useMemo(() => [DEFAULT_YEAR - 1, DEFAULT_YEAR, DEFAULT_YEAR + 1], []);

  const loadAvailableMonths = async (selectedYear) => {
    const response = await fetchMonthlyAvailable(selectedYear);
    const months = response?.months || [];
    setAvailableMonths(months);
    if (months.length && !months.includes(month)) setMonth(months[months.length - 1]);
  };

  const loadData = async () => {
    setLoading(true);
    setData(await fetchMonthlyWinner(leagueId, year, month));
    setLoading(false);
  };

  useEffect(() => {
    loadAvailableMonths(year);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month, leagueId]);

  const winner = data?.winner || null;
  const breakdown = data?.winner_gw_breakdown || [];
  const leagueRank = data?.winner_league_rank ?? winner?.league_rank ?? null;
  const selectedMonth = monthLabel(data?.month || month);
  const firstGw = breakdown[0]?.gw ?? null;
  const lastGw = breakdown[breakdown.length - 1]?.gw ?? firstGw;
  const bestGw = breakdown.reduce(
    (best, item) => (!best || Number(item.points || 0) > Number(best.points || 0) ? item : best),
    null
  );
  const averageScore = breakdown.length
    ? Math.round(breakdown.reduce((sum, item) => sum + Number(item.net || 0), 0) / breakdown.length)
    : 0;
  const totalHits = breakdown.reduce((sum, item) => sum + Number(item.hits || 0), 0);
  const chipsUsed = breakdown.filter((item) => item.chip).length;

  return (
    <div className={`motm-page ${competitionClass}`}>
      <div className="motm-shot-toolbar">
        <div>
          <span>FPL Nepal · {competitionName}</span>
          <h2>Manager of the Month</h2>
        </div>
        <div className="motm-controls">
          <label>
            <span>Month</span>
            <select value={month} onChange={(event) => setMonth(Number(event.target.value))}>
              {availableMonths.map((item) => <option key={item} value={item}>{monthLabel(item)}</option>)}
            </select>
          </label>
          <label>
            <span>Year</span>
            <select value={year} onChange={(event) => setYear(Number(event.target.value))}>
              {years.map((item) => <option key={item} value={item}>{item}</option>)}
            </select>
          </label>
          <button onClick={loadData} disabled={loading}>
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />Refresh
          </button>
        </div>
      </div>

      {loading ? <LoadingCard /> : winner ? (
        <>
          <section className="motm-shot-poster">
            <div className="motm-shot-confetti" aria-hidden="true">
              {Array.from({ length: 18 }).map((_, index) => <span key={index} />)}
            </div>

            <div className="motm-shot-winner">
              <div className="motm-shot-brand">FPL NEPAL · {competitionName}</div>
              <div className="motm-shot-kicker"><Crown size={17} /> Monthly champion</div>
              <h3>MANAGER OF<br />THE MONTH</h3>

              <div className="motm-shot-manager">
                <span>Winning manager</span>
                <strong>{winner.manager_name}</strong>
                <small>{winner.team_name}</small>
              </div>

              <div className="motm-shot-summary">
                <div>
                  <span>Monthly score</span>
                  <strong>{winner.monthly_net_points}<small> pts</small></strong>
                </div>
                <div>
                  <span>League position</span>
                  <strong>#{leagueRank || "—"}</strong>
                </div>
              </div>

              <div className="motm-shot-footer">
                <b>Congratulations</b>
                <span>{selectedMonth} Manager of the Month · FPL Nepal</span>
              </div>
            </div>

            <div className="motm-shot-gameweeks">
              <div className="motm-shot-month-overview">
                <div>
                  <span><CalendarDays size={13} /> The winning month</span>
                  <h4>{selectedMonth} <small>{year}</small></h4>
                  <p>
                    {firstGw === lastGw ? `Gameweek ${firstGw}` : `Gameweeks ${firstGw}–${lastGw}`}
                  </p>
                </div>
                <b>{breakdown.map((item) => `GW${item.gw}`).join(" · ")}</b>
              </div>
              <div className={`motm-shot-gw-grid motm-shot-gw-count-${breakdown.length}`}>
                {breakdown.map((item) => <CompactGwCard key={item.gw} item={item} />)}
              </div>
              <div className="motm-shot-month-stats">
                <div>
                  <span>Best Gameweek</span>
                  <strong>GW{bestGw?.gw ?? "—"} <small>{bestGw ? `${bestGw.points} pts` : "—"}</small></strong>
                </div>
                <div>
                  <span>Average score</span>
                  <strong>{averageScore} <small>pts / GW</small></strong>
                </div>
                {totalHits > 0 ? (
                  <div className="motm-shot-stat-hit">
                    <span>Transfer hits</span>
                    <strong>−{totalHits} <small>points</small></strong>
                  </div>
                ) : (
                  <div>
                    <span>{chipsUsed ? "Chips used" : "Gameweeks"}</span>
                    <strong>{chipsUsed || breakdown.length} <small>{chipsUsed ? (chipsUsed === 1 ? "chip" : "chips") : "played"}</small></strong>
                  </div>
                )}
              </div>
              <div className="motm-shot-gameweeks-note">Monthly total includes transfer-hit deductions.</div>
            </div>
          </section>

          <section className="motm-standings-section">
            <div className="motm-section-heading">
              <div><span>MONTHLY TABLE</span><h3>{selectedMonth} Standings</h3></div>
              <p>Scores include transfer deductions.</p>
            </div>
            <div className="motm-table-wrap">
              <table>
                <thead><tr><th>Rank</th><th>Manager</th><th>Team</th><th>League position</th><th>Monthly points</th></tr></thead>
                <tbody>
                  {(data?.standings || []).map((row, index) => (
                    <tr key={row.manager_id} className={index === 0 ? "motm-table-winner" : ""}>
                      <td><span className={`motm-rank motm-rank-${Math.min(index + 1, 4)}`}>{row.rank}</span></td>
                      <td><b>{row.manager_name}</b>{index === 0 && <small>Manager of the Month</small>}</td>
                      <td>{row.team_name}</td><td>#{row.league_rank ?? "—"}</td>
                      <td><strong>{row.monthly_net_points}</strong> pts</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      ) : (
        <div className="motm-empty"><Trophy size={36} /><h3>No monthly result yet</h3><p>No Gameweek data is available for {selectedMonth} {year}.</p></div>
      )}
    </div>
  );
}
